import { useEffect, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { ChevronsUpDown, CircleHelp, Loader2, Package, Pencil, X } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { CardmarketExpansionRow, Set } from '@/types/cardCatalog'
import { updateCardmarketExpansion } from '@/services/cardService'
import { CardmarketExpansionProductsDialog } from './CardmarketExpansionProductsDialog'

function setOptionLabel(s: Set): string {
  return `${s.locale.toUpperCase()} ${s.code} · ${s.name}`
}

function formatSetsCell(sets: Set[]): string {
  if (sets.length === 0) {
    return '—'
  }
  return sets.map((s) => setOptionLabel(s)).join(', ')
}

/** Add one catalog set that is not already in `selectedIds`. */
function CatalogSetAddCombobox({
  selectedIds,
  catalogSets,
  onAdd,
  disabled,
}: {
  selectedIds: string[]
  catalogSets: Set[]
  onAdd: (setId: string) => void
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const available = catalogSets.filter((s) => !selectedIds.includes(s.id))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Add linked catalog set"
          disabled={disabled || available.length === 0}
          className={cn('h-8 w-full min-w-[200px] justify-between font-normal text-sm', 'bg-white text-gray-900 hover:bg-gray-100')}
        >
          <span className="truncate text-left text-muted-foreground">Add catalog set…</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('p-0 w-[min(320px,calc(100vw-2rem))]', 'border border-gray-200 bg-white text-gray-900 shadow-md')} align="start">
        <Command>
          <CommandInput placeholder="Search sets…" />
          <CommandList>
            <CommandEmpty>{available.length === 0 ? 'All listed sets are already linked.' : 'No matching set.'}</CommandEmpty>
            <CommandGroup>
              {available.map((s) => {
                const text = setOptionLabel(s)
                return (
                  <CommandItem
                    key={s.id}
                    value={`${s.id} ${text}`}
                    onSelect={() => {
                      onAdd(s.id)
                      setOpen(false)
                    }}
                  >
                    {text}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function CardmarketExpansionRowReadOnly({
  row,
  onEdit,
  onShowProducts,
}: {
  row: CardmarketExpansionRow
  onEdit: () => void
  onShowProducts: () => void
}) {
  return (
    <TableRow>
      <TableCell className="font-mono text-xs tabular-nums">{row.expansionId}</TableCell>
      <TableCell className="min-w-[140px]">{row.name?.trim() ? row.name : '—'}</TableCell>
      <TableCell className="min-w-[220px] text-sm wrap-break-word">{formatSetsCell(row.sets)}</TableCell>
      <TableCell className="w-[100px]">
        <div className="flex items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={`Show cached singles and non-singles for expansion ${row.expansionId}`}
                onClick={onShowProducts}
              >
                <Package className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Singles and non-singles (cached list)</TooltipContent>
          </Tooltip>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label={`Edit expansion ${row.expansionId}`} onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

function CardmarketExpansionRowEditor({ row, catalogSets, onSaved, onCancel }: { row: CardmarketExpansionRow; catalogSets: Set[]; onSaved: () => void; onCancel: () => void }) {
  const [name, setName] = useState(row.name ?? '')
  const [setIds, setSetIds] = useState<string[]>(() => row.sets.map((s) => s.id))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    setName(row.name ?? '')
    setSetIds(row.sets.map((s) => s.id))
  }, [row.id, row.name, row.sets, row.updatedAt])

  function removeSet(id: string) {
    setSetIds((prev) => prev.filter((x) => x !== id))
  }

  function addSet(id: string) {
    setSetIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }

  async function save() {
    setSaving(true)
    setErr(null)
    try {
      await updateCardmarketExpansion(row.id, {
        name: name.trim() === '' ? null : name.trim(),
        setIds,
      })
      onSaved()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <TableRow>
      <TableCell className="font-mono text-xs tabular-nums">{row.expansionId}</TableCell>
      <TableCell className="min-w-[140px]">
        <Input className="h-8 text-sm" value={name} onChange={(e) => setName(e.target.value)} disabled={saving} aria-label="Expansion display name" />
        {err && <p className="text-xs text-destructive mt-1">{err}</p>}
      </TableCell>
      <TableCell className="min-w-[240px]">
        <div className="flex flex-col gap-2">
          <ul className="flex flex-col gap-1" aria-label="Linked catalog sets">
            {setIds.map((id) => {
              const s = catalogSets.find((c) => c.id === id)
              const label = s ? setOptionLabel(s) : `Unknown (${id.slice(0, 8)}…)`
              return (
                <li key={id} className="flex items-center justify-between gap-1 rounded border border-border bg-muted/30 px-2 py-1 text-xs">
                  <span className="min-w-0 wrap-break-word">{label}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    disabled={saving}
                    aria-label={`Remove ${label}`}
                    onClick={() => removeSet(id)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </li>
              )
            })}
          </ul>
          <CatalogSetAddCombobox selectedIds={setIds} catalogSets={catalogSets} onAdd={addSet} disabled={saving} />
        </div>
      </TableCell>
      <TableCell className="w-[100px]">
        <div className="flex flex-wrap gap-1">
          <Button type="button" size="sm" variant="secondary" disabled={saving} onClick={() => void save()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </TableCell>
    </TableRow>
  )
}

export type CardMarketExpansionsTableProps = {
  items: CardmarketExpansionRow[]
  catalogSets: Set[]
  onRefresh: () => void
}

export function CardMarketExpansionsTable({ items, catalogSets, onRefresh }: CardMarketExpansionsTableProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [productsFor, setProductsFor] = useState<{ expansionId: number; label: string } | null>(null)

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">Exp. ID</TableHead>
            <TableHead className="min-w-[120px]">
              <div className="flex items-center gap-0.5">
                <span>Name</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground" aria-label="Name column help">
                      <CircleHelp className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    Name of the expansion on cardmarket
                  </TooltipContent>
                </Tooltip>
              </div>
            </TableHead>
            <TableHead className="min-w-[220px]">Catalog sets</TableHead>
            <TableHead className="w-[120px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center text-muted-foreground">
                No expansion rows in the database yet.
              </TableCell>
            </TableRow>
          ) : (
            items.map((row) =>
              editingId === row.id ? (
                <CardmarketExpansionRowEditor
                  key={row.id}
                  row={row}
                  catalogSets={catalogSets}
                  onSaved={() => {
                    setEditingId(null)
                    onRefresh()
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <CardmarketExpansionRowReadOnly
                  key={row.id}
                  row={row}
                  onEdit={() => setEditingId(row.id)}
                  onShowProducts={() =>
                    setProductsFor({
                      expansionId: row.expansionId,
                      label: [row.name?.trim(), formatSetsCell(row.sets) !== '—' ? formatSetsCell(row.sets) : null].filter(Boolean).join(' · ') || '',
                    })
                  }
                />
              ),
            )
          )}
        </TableBody>
      </Table>
      <CardmarketExpansionProductsDialog
        open={productsFor != null}
        onOpenChange={(o) => {
          if (!o) setProductsFor(null)
        }}
        expansionId={productsFor?.expansionId ?? 0}
        expansionLabel={productsFor?.label ?? ''}
      />
    </div>
  )
}
